import Highcharts from 'highcharts'
import HighchartsMore from 'highcharts/highcharts-more'
import HighchartsSunburst from 'highcharts/modules/sunburst'
import HighchartsTreemap from 'highcharts/modules/treemap'
import HighchartsTreegraph from 'highcharts/modules/treegraph'
import HighchartsExporting from 'highcharts/modules/exporting'

import '@aytm/app/stats/stores/skipperChatAssistants'

import { registerDataQualityReportView } from '@aytm/app/stats/page/data-quality-report/controller/shim'
import { GuidedReportStatusPoller } from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReportStatus'
import { resolveReportParams } from '@aytm/app/stats/page/question-views/vk/lib/report-params'

HighchartsMore(Highcharts)
HighchartsSunburst(Highcharts)
HighchartsTreemap(Highcharts)
HighchartsTreegraph(Highcharts)
HighchartsExporting(Highcharts)

import {
  cleanSearchData,
  getIsDefaultReportParams,
  getFilteredQuestions
} from '@aytm/app/stats/page/comments/lib/utils'

import { RenameAllCodebooks, RenameAllTags } from '@aytm/app/stats/page/sidebar/reports/actions'

import exceptionReporter from '@aytm/lib/common/exception-reporter'

import * as mdControlsUtils from '@aytm/app/stats/page/question-views/md/lib/controls-utils'
import * as compareItemsUtils from '@aytm/app/stats/page/question-views/info-view/components/compare-items/lib/compare-items-utils'
import { calcSavedPatternData as calcVqSavedPatternData } from '@aytm/app/stats/page/question-views/vq/lib/saved-pattern-data'

// The payload + owned report params a virtual-question partial works out per variant.
import * as vqModelData from '@aytm/app/stats/page/question-views/vq/lib/model-data'

// The sentiments-tags charts-model transform the consolidated block.coffee calls.
import * as oeChartsModel from '@aytm/app/stats/page/question-views/oe/lib/chartsModel'
// The answer-variant axis maths, for the blocks not yet drawing their chart in React.
import * as oeDisplayRange from '@aytm/app/stats/page/question-views/oe/lib/display-range'
import * as scrollAnchor from '@aytm/app/stats/lib/page/scroll-anchor'
import * as heldBackVariables from '@aytm/app/stats/lib/page/held-back-variables'
import * as cxScope from '@aytm/app/stats/page/question-views/cx/lib/scope'
// The pins / clusters a heatmap expands, drawn as answer variants -- the shell owns a set of
// report params per row, so it resolves the same list the rows are drawn from.
import * as hmAnswerVariants from '@aytm/app/stats/page/question-views/hm/lib/props/variants'
// A cluster that is gone leaves a cloud param naming nothing behind it; the shell sweeps them.
import * as hmClusters from '@aytm/app/stats/page/question-views/hm/lib/clusters'

// The wc<id> param grammar, shared by every block that draws a word cloud.
import * as wcReportParams from '@aytm/app/stats/page/question-views/components/word-cloud/lib/report-params'

import { viewerIsGuest, viewerModelFields, viewerProps } from '@aytm/app/stats/lib/page/viewer'
import { getCSRFToken } from '@aytm/lib/common/rails-post-fetch'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { getFeatures } from '@aytm/app/stats/lib/stat-page'

// Pure view-mode / export-option helpers shared by the MaxDiff controls and the host shell
// that resolves the persisted view mode before render.
import '@aytm/app/stats/page/styles/globals.scss'
import '@aytm/app/stats/page/question-views/components/charts/highcharts/styles/highcharts.scss'

import { installKeyboardMode } from '@aytm/app/stats/lib/page/keyboard-mode'

// the stats styles give a container its hover look under keyboard focus through this mark
installKeyboardMode()

function renderComponent(props, element, path, component = 'default') {
  return new Promise((resolve, reject) => {
    if (null === element) {
      reject('element not specified')
      return
    }
    const React = require('react')
    const ReactDOM = require('react-dom')
    const reactEl = React.createElement(require(`@aytm/app/stats/page/${path}`)[component], props)
    try {
      resolve(ReactDOM.render(reactEl, element))
    } catch (error) {
      // React's DOM reconciliation can fail if external code (browser extensions,
      // Backbone views) modified DOM nodes that React tracks (e.g. portal targets).
      // Report, clear the corrupted tree, and render fresh.
      exceptionReporter(error, 'renderComponent', path)
      try {
        ReactDOM.unmountComponentAtNode(element)
      } catch {
        // unmount may also fail if the fiber tree is inconsistent
      }
      element.textContent = ''
      resolve(ReactDOM.render(reactEl, element))
    }
  })
}

export function unlinkComponent(element) {
  return new Promise((resolve, reject) => {
    if (null === element) {
      reject('element not specified')
      return
    }
    const ReactDOM = require('react-dom')
    // Only unmount when a top-level React root was actually rendered here. Backbone teardown
    // (destroyReactComponents) calls this on every destroyed view's @el; when that node is one
    // React owns as part of a parent tree — a Backbone element nested inside a React root during
    // the stat-backbone-react migration — unmountComponentAtNode is a no-op that logs a dev warning
    // ("… is not a top-level container"). `_reactRootContainer` is the exact flag React checks to
    // take the root branch, so guarding on it skips only the warning-producing non-root case.
    resolve(Boolean(element._reactRootContainer) && ReactDOM.unmountComponentAtNode(element))
  })
}

export function renderSpecials(props, element) {
  // Same reason as the sidebar below: computed here instead of in the legacy shell, onto the props
  // object it already holds. The token comes from the shared Rails helper rather than the page
  // object, so the surface no longer needs the shell to hand it one.
  if (props) Object.assign(props, viewerProps(), { CSRF: getCSRFToken() })

  return renderComponent(props, element, 'comments/index')
}

export function renderSidebar(props, element) {
  // Filled here rather than handed in: the legacy shell used to compute these from the globals and
  // thread them down. Assigned onto the model it already holds, so its identity is kept and the
  // panels reading `model.isAdmin` are untouched.
  if (props && props.model) Object.assign(props.model, viewerModelFields())

  return renderComponent(props, element, 'sidebar/Sidebar')
}

export function renderContentHeader(props, element) {
  return renderComponent(props, element, 'components/ContentHeader')
}

export function renderSkipperReportBanner(props, element) {
  return renderComponent(props, element, 'components/SkipperReportBanner')
}

export function renderQuestionFooter(props, element) {
  return renderComponent(props, element, 'question-views/components/QuestionFooter')
}

export function renderQuestionHeader(props, element) {
  return renderComponent(props, element, 'question-views/components/QuestionHeader')
}

// The run band, for a block that draws nothing else -- every other host renders it inside its own
// React block.
export function renderDataRun(props, element) {
  return renderComponent(props, element, 'question-views/components/data-run/DataRun')
}

export function renderVQComponent(props, element) {
  return renderComponent(props, element, 'question-views/vq/Component')
}

export function renderCQComponent(props, element) {
  return renderComponent(props, element, 'question-views/cq/Component')
}

export function renderXQComponent(props, element) {
  return renderComponent(props, element, 'question-views/xq/Component')
}

export function renderCLComponent(props, element) {
  return renderComponent(props, element, 'question-views/cl/Component')
}

export function renderTLComponent(props, element) {
  return renderComponent(props, element, 'question-views/tl/Component')
}

export function renderSNComponent(props, element) {
  return renderComponent(props, element, 'question-views/sn/Component')
}

export function renderCLMappingComponent(props, element) {
  return renderComponent(props, element, 'question-views/cl-mapping/Component')
}

export function renderTLMappingComponent(props, element) {
  return renderComponent(props, element, 'question-views/tl-mapping/Component')
}

export function renderHMBlock(props, element) {
  // The roles come from the boot values and the features from the decorated registry, which is the
  // live one — the raw served payload is not what a reader of this block should see.
  if (props) Object.assign(props, { userRoles: PageGlobals.USER_ROLES || [], features: getFeatures() })

  return renderComponent(props, element, 'question-views/hm/Block')
}

export function renderOEXBlock(props, element) {
  return renderComponent(props, element, 'question-views/oex/Block')
}

export function renderAVBlock(props, element) {
  return renderComponent(props, element, 'question-views/answer-variants/Block')
}

export function renderECBlock(props, element) {
  if (props) props.confidenceLevels = PageGlobals.CONFIDENCE_LEVELS

  return renderComponent(props, element, 'question-views/ec/Block')
}

export function renderSHBlock(props, element) {
  if (props) props.confidenceLevels = PageGlobals.CONFIDENCE_LEVELS

  return renderComponent(props, element, 'question-views/sh/Block')
}

export function renderBPMBlock(props, element) {
  return renderComponent(props, element, 'question-views/bpm/Block')
}

export function renderEMDBlock(props, element) {
  return renderComponent(props, element, 'question-views/emd/Block')
}

// The two conjoint question types mount their own block; both share the body and the components
// under question-views/cjc.
export function renderCJCBlock(props, element) {
  return renderComponent(props, element, 'question-views/cjc/Block')
}

export function renderCJDBlock(props, element) {
  return renderComponent(props, element, 'question-views/cjd/Block')
}

export function renderITBlock(props, element) {
  if (props) props.isSurveyGuest = viewerIsGuest()

  return renderComponent(props, element, 'question-views/it/Block')
}

export function renderIMBlock(props, element) {
  if (props) props.isSurveyGuest = viewerIsGuest()

  return renderComponent(props, element, 'question-views/im/Block')
}

export function renderIRBlock(props, element) {
  if (props) props.isSurveyGuest = viewerIsGuest()

  return renderComponent(props, element, 'question-views/ir/Block')
}

export function renderVOBlock(props, element) {
  if (props) props.isSurveyGuest = viewerIsGuest()

  return renderComponent(props, element, 'question-views/vo/Block')
}

export function renderVABlock(props, element) {
  if (props) props.isSurveyGuest = viewerIsGuest()

  return renderComponent(props, element, 'question-views/va/Block')
}

export function renderEDBlock(props, element) {
  if (props) props.isSurveyGuest = viewerIsGuest()

  return renderComponent(props, element, 'question-views/ed/Block')
}

export function renderVKBlock(props, element) {
  return renderComponent(props, element, 'question-views/vk/Block')
}

export function renderPSCBlock(props, element) {
  if (props) props.isSurveyGuest = viewerIsGuest()

  return renderComponent(props, element, 'question-views/psc/Block')
}

export function renderADBlock(props, element) {
  if (props) props.isSurveyGuest = viewerIsGuest()

  return renderComponent(props, element, 'question-views/ad/Block')
}

// Every matrix-family flavour draws a block of its own: the strip above the chart and the list
// below it are what they differ in, so the shell mounts the one its question type names.
export function renderMXBlock(props, element) {
  return renderComponent(props, element, 'question-views/mx/Block')
}

export function renderSRBlock(props, element) {
  return renderComponent(props, element, 'question-views/sr/Block')
}

export function renderSEBlock(props, element) {
  return renderComponent(props, element, 'question-views/se/Block')
}

export function renderROBlock(props, element) {
  return renderComponent(props, element, 'question-views/ro/Block')
}

export function renderAROBlock(props, element) {
  return renderComponent(props, element, 'question-views/aro/Block')
}

export function renderPSBlock(props, element) {
  return renderComponent(props, element, 'question-views/ps/Block')
}

export function renderMPBlock(props, element) {
  return renderComponent(props, element, 'question-views/mp/Block')
}

export function renderRABlock(props, element) {
  return renderComponent(props, element, 'question-views/ra/Block')
}

export function renderMDBlock(props, element) {
  if (props) {
    props.confidenceLevels = PageGlobals.CONFIDENCE_LEVELS
    props.specialExport = getFeatures().special_export || {}
  }

  return renderComponent(props, element, 'question-views/md/Block')
}

export function renderOEBlock(props, element) {
  return renderComponent(props, element, 'question-views/oe/Block')
}

export function renderCXBlock(props, element) {
  return renderComponent(props, element, 'question-views/cx/Block')
}

export function renderCNBlock(props, element) {
  return renderComponent(props, element, 'question-views/cn/Block')
}

export function renderMNBlock(props, element) {
  if (props) props.confidenceLevels = PageGlobals.CONFIDENCE_LEVELS

  return renderComponent(props, element, 'question-views/mn/Block')
}

export function renderCQPartial(props, element) {
  if (props?.data) props.data.confidenceLevels = PageGlobals.CONFIDENCE_LEVELS

  return renderComponent(props, element, 'question-views/cq/components/partial/Block')
}

export function renderCLPartial(props, element) {
  return renderComponent(props, element, 'question-views/cl/components/partial/Block')
}

export function renderTLPartial(props, element) {
  return renderComponent(props, element, 'question-views/tl/components/partial/Block')
}

export function renderInfoViewHistogram(props, element) {
  return renderComponent(props, element, 'question-views/info-view/components/charts/histogram/Block')
}

export function renderInfoViewBars(props, element) {
  if (props) props.confidenceLevels = PageGlobals.CONFIDENCE_LEVELS

  return renderComponent(props, element, 'question-views/info-view/components/charts/bars/Block')
}

export function renderInfoViewOexCols(props, element) {
  return renderComponent(props, element, 'question-views/info-view/components/charts/oex-cols/Block')
}

export function renderInfoViewControl(props, element) {
  return renderComponent(props, element, 'question-views/info-view/components/Control')
}

export function renderInfoViewTable(props, element) {
  if (props) props.isSurveyGuest = viewerIsGuest()

  return renderComponent(props, element, 'question-views/info-view/components/Table')
}

export function renderInfoViewText(props, element) {
  return renderComponent(props, element, 'question-views/info-view/components/Text')
}

export function renderInfoViewImage(props, element) {
  return renderComponent(props, element, 'question-views/info-view/components/Image')
}

export function renderInfoViewGrid(props, element) {
  return renderComponent(props, element, 'question-views/info-view/components/charts/grid/Block')
}

export function renderInfoViewHorizontalBars(props, element) {
  return renderComponent(props, element, 'question-views/info-view/components/charts/horizontal-bars/Block')
}

export function renderInfoViewBPM(props, element) {
  return renderComponent(props, element, 'question-views/info-view/components/charts/bpm/Block')
}

export function renderInfoViewQuadrant(props, element) {
  return renderComponent(props, element, 'question-views/info-view/components/charts/quadrant/Block')
}

export function renderInfoViewCorrelationFinder(props, element) {
  if (props?.data) props.data.confidenceLevels = PageGlobals.CONFIDENCE_LEVELS

  return renderComponent(props, element, 'question-views/info-view/components/charts/correlation-finder/Block')
}

export function renderInfoViewCompareItems(props, element) {
  return renderComponent(props, element, 'question-views/info-view/components/compare-items/Block')
}

// Coffee-callable factory: builds (does not start) a guided-report status poller.
// reports_panel.coffee owns the start/stop lifecycle via its poller registry.
export function createGuidedReportStatusPoller(reportId, callbacks) {
  return new GuidedReportStatusPoller(reportId, callbacks)
}

export function initializeXpertsDebug() {
  return new Promise((resolve, _reject) => {
    const id = 'xperts-debug'
    let element = document.querySelector(`#${id}`)
    const exists = null !== element
    if (!exists) {
      element = document.createElement('div')
      element.id = id
      element.style.position = 'absolute'
      element.style.width = '100%'
      element.style.height = '80%'
      element.style.margin = '0 0 0 -100%'
      element.style.textAlign = 'left'
      element.style.zIndex = 1000
      document.querySelector('.-main').appendChild(element)
    }
    if (element) {
      resolve(
        renderXpertsDebug({ text: 'Drop .json file here or click anywhere to open "File open..." dialog' }, element)
      )
    }
  })
}

export function renderXpertsDebug(props, element) {
  return renderComponent(props, element, 'xperts-debug/XpertsDebugPopup')
}

// The deck gathers a question's slides at the moment Download is pressed, from whatever the block can
// answer then. This block's substance -- the pricing configuration and the period pair -- lives in the
// rendered half, so a block the reader has not reached yet has none of it and its slides cannot be
// built. The same state is already on the page as this question's stored section, and the resolver
// below is the one the block itself mounts with, so what it rebuilds is what the block would hold.
export function vkExportDataFromParams({ section, format, components, answer }) {
  const { period, fqPeriod, infographic, config } = resolveReportParams({ section, format, components, answer })

  return { period, fq_period: fqPeriod, infographic, config }
}

if ('undefined' != typeof window) {
  window['Highcharts'] = Highcharts

  window['$STATS'] || (window['$STATS'] = {})

  window['$STATS']['mdControlsUtils'] = mdControlsUtils
  window['$STATS']['compareItemsUtils'] = compareItemsUtils
  window['$STATS']['calcVqSavedPatternData'] = calcVqSavedPatternData
  window['$STATS']['vqModelData'] = vqModelData

  window['$STATS']['oeChartsModel'] = oeChartsModel
  window['$STATS']['oeDisplayRange'] = oeDisplayRange
  window['$STATS']['scrollAnchor'] = scrollAnchor
  window['$STATS']['heldBackVariables'] = heldBackVariables
  window['$STATS']['cxScope'] = cxScope
  window['$STATS']['hmAnswerVariants'] = hmAnswerVariants
  window['$STATS']['hmClusters'] = hmClusters
  window['$STATS']['wcReportParams'] = wcReportParams

  window['$STATS']['renderSpecials'] = renderSpecials

  window['$STATS']['renderSidebar'] = renderSidebar

  window['$STATS']['renderContentHeader'] = renderContentHeader

  window['$STATS']['renderSkipperReportBanner'] = renderSkipperReportBanner
  window['$STATS']['createGuidedReportStatusPoller'] = createGuidedReportStatusPoller

  window['$STATS']['renderQuestionHeader'] = renderQuestionHeader
  window['$STATS']['renderQuestionFooter'] = renderQuestionFooter

  window['$STATS']['renderDataRun'] = renderDataRun

  window['$STATS']['renderVQComponent'] = renderVQComponent
  window['$STATS']['renderCQComponent'] = renderCQComponent
  window['$STATS']['renderCQPartial'] = renderCQPartial
  window['$STATS']['renderCLPartial'] = renderCLPartial
  window['$STATS']['renderTLPartial'] = renderTLPartial
  window['$STATS']['renderXQComponent'] = renderXQComponent
  window['$STATS']['renderCLComponent'] = renderCLComponent
  window['$STATS']['renderTLComponent'] = renderTLComponent
  window['$STATS']['renderSNComponent'] = renderSNComponent

  window['$STATS']['renderCLMappingComponent'] = renderCLMappingComponent
  window['$STATS']['renderTLMappingComponent'] = renderTLMappingComponent

  window['$STATS']['renderBPMBlock'] = renderBPMBlock
  window['$STATS']['renderEMDBlock'] = renderEMDBlock

  window['$STATS']['renderITBlock'] = renderITBlock
  window['$STATS']['renderIMBlock'] = renderIMBlock
  window['$STATS']['renderIRBlock'] = renderIRBlock
  window['$STATS']['renderVOBlock'] = renderVOBlock
  window['$STATS']['renderVABlock'] = renderVABlock
  window['$STATS']['renderEDBlock'] = renderEDBlock

  window['$STATS']['renderVKBlock'] = renderVKBlock

  window['$STATS']['renderPSCBlock'] = renderPSCBlock
  window['$STATS']['renderADBlock'] = renderADBlock

  window['$STATS']['renderECBlock'] = renderECBlock
  window['$STATS']['renderSHBlock'] = renderSHBlock

  window['$STATS']['renderHMBlock'] = renderHMBlock

  window['$STATS']['renderCJCBlock'] = renderCJCBlock
  window['$STATS']['renderCJDBlock'] = renderCJDBlock

  window['$STATS']['renderMXBlock'] = renderMXBlock
  window['$STATS']['renderSRBlock'] = renderSRBlock
  window['$STATS']['renderSEBlock'] = renderSEBlock
  window['$STATS']['renderROBlock'] = renderROBlock
  window['$STATS']['renderAROBlock'] = renderAROBlock
  window['$STATS']['renderPSBlock'] = renderPSBlock
  window['$STATS']['renderMPBlock'] = renderMPBlock
  window['$STATS']['renderRABlock'] = renderRABlock
  window['$STATS']['renderMDBlock'] = renderMDBlock

  window['$STATS']['renderOEBlock'] = renderOEBlock
  window['$STATS']['renderCXBlock'] = renderCXBlock
  window['$STATS']['renderCNBlock'] = renderCNBlock

  window['$STATS']['renderMNBlock'] = renderMNBlock
  window['$STATS']['renderOEXBlock'] = renderOEXBlock
  window['$STATS']['renderAVBlock'] = renderAVBlock

  window['$STATS']['renderInfoViewControl'] = renderInfoViewControl
  window['$STATS']['renderInfoViewTable'] = renderInfoViewTable
  window['$STATS']['renderInfoViewText'] = renderInfoViewText
  window['$STATS']['renderInfoViewImage'] = renderInfoViewImage
  window['$STATS']['renderInfoViewGrid'] = renderInfoViewGrid
  window['$STATS']['renderInfoViewHorizontalBars'] = renderInfoViewHorizontalBars
  window['$STATS']['renderInfoViewBPM'] = renderInfoViewBPM
  window['$STATS']['renderInfoViewQuadrant'] = renderInfoViewQuadrant
  window['$STATS']['renderInfoViewCorrelationFinder'] = renderInfoViewCorrelationFinder
  window['$STATS']['renderInfoViewCompareItems'] = renderInfoViewCompareItems
  window['$STATS']['renderInfoViewOexCols'] = renderInfoViewOexCols
  window['$STATS']['renderInfoViewBars'] = renderInfoViewBars
  window['$STATS']['renderInfoViewHistogram'] = renderInfoViewHistogram

  window['$STATS']['unlinkComponent'] = unlinkComponent

  window['$STATS']['XpertsDebug'] = initializeXpertsDebug
  window['$STATS']['renderXpertsDebug'] = renderXpertsDebug

  window['$STATS']['RenameAllCodebooks'] = RenameAllCodebooks
  window['$STATS']['RenameAllTags'] = RenameAllTags

  window['$STATS']['cleanSearchData'] = cleanSearchData
  window['$STATS']['isSpecialsDefault'] = getIsDefaultReportParams
  window['$STATS']['getFilteredQuestions'] = getFilteredQuestions

  window['$STATS']['vkExportDataFromParams'] = vkExportDataFromParams

  // Initialize ChatButton directly when $STATS is ready
  window['$STATS']['initChatButton'] = function () {
    if (window.$STATS?.skipperChatAssistants) {
      return window.$STATS.skipperChatAssistants.initialize()
    }
    return false
  }

  // Register the DQR bridge as page.classes.DataQualityView before page_router constructs it
  // (page.classes uses ||= so order is safe).
  registerDataQualityReportView()
}
